import { jsx as _jsx } from "react/jsx-runtime";
import { forwardRef, useContext, useMemo } from 'react';
import { MaterialContext } from './component-types.js';
import { Panel } from './Panel.js';
/**
 * An Image component that renders a textured panel with an image source.
 * Supports both URL strings and Texture objects as image sources.
 *
 * @component
 * @example
 * ```tsx
 * // Using a URL
 * <Image src="/path/to/image.png" width={100} height={100} />
 *
 * // Using a Texture object
 * <Image src={myTexture} width={100} height={100} />
 * ```
 *
 * @param {Object} props - The component props
 * @param {string | Texture} props.src - The image source, either a URL string or a Texture object
 * @param {Material | null} [props.material] - Optional custom material. If not provided, uses cloned textured panel material from context
 * @param {React.ReactNode} props.children - Child elements to render on top of the image
 * @param {React.Ref<Object3D>} ref - Forward ref to access the underlying 3D object
 * @returns {React.ReactElement} An image panel element
 */
export const Image = forwardRef((props, ref) => {
    var _a;
    const context = useContext(MaterialContext);
    const mat = (_a = props.material) !== null && _a !== void 0 ? _a : useMemo(() => { var _a; return (_a = context.panelMaterialTextured) === null || _a === void 0 ? void 0 : _a.clone(); }, []);
    const texture = typeof props.src === 'string'
        ? useMemo(() => mat.engine.textures.load(props.src), [props.src])
        : Promise.resolve(props.src);
    texture.then((t) => (mat.flatTexture = t));
    return (_jsx(Panel, Object.assign({}, props, { material: mat, ref: ref, children: props.children })));
});
Image.displayName = 'Image';
